import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, Area, AreaChart, PieChart, Pie, Cell 
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import StatCard from '../ui/StatCard';
import { formatCurrency } from '../../lib/utils';
import api from '../../lib/api';

const COLORS = {
  primary: '#8b5cf6',
  secondary: '#ec4899',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6'
};

const PIE_COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#3b82f6', '#10b981'];

const EntrepriseAnalyticsTab = () => {
  const [loading, setLoading] = useState(true);
  const [periodDays, setPeriodDays] = useState(30);
  const [consommationData, setConsommationData] = useState([]);
  const [evolutionSolde, setEvolutionSolde] = useState([]);
  const [topEmployes, setTopEmployes] = useState([]);
  const [coutTotal, setCoutTotal] = useState(null);
  const [soldeActuel, setSoldeActuel] = useState(0);

  useEffect(() => {
    loadAnalytics();
  }, [periodDays]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      // Charger consommation quotidienne
      const consommationResponse = await api.get(`/entreprise/analytics/consommation?days=${periodDays}`);
      setConsommationData(consommationResponse.data.consommation);
      
      // Charger évolution du solde
      const soldeResponse = await api.get(`/entreprise/analytics/evolution-solde?days=${periodDays}`);
      setEvolutionSolde(soldeResponse.data.evolution);
      setSoldeActuel(soldeResponse.data.solde_actuel);
      
      // Charger top 10 employés
      const topResponse = await api.get(`/entreprise/analytics/top-employes?limit=10&period_days=${periodDays}`);
      setTopEmployes(topResponse.data.top_employes);
      
      // Charger coût total
      const coutResponse = await api.get(`/entreprise/analytics/cout-total?period_days=${periodDays}`);
      setCoutTotal(coutResponse.data);
      
    } catch (error) {
      console.error('Erreur chargement analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  // Calculer les données pour le graphique camembert (répartition avances/commissions)
  const pieData = coutTotal ? [
    { name: 'Avances versées', value: coutTotal.total_avances },
    { name: 'Commissions', value: coutTotal.total_commissions }
  ] : [];

  // Calculer les tendances
  const calculateTrend = (data, key) => {
    if (!data || data.length < 2) return { trend: 'neutral', value: 'N/A' };
    const recent = data.slice(-7).reduce((sum, item) => sum + (item[key] || 0), 0);
    const previous = data.slice(-14, -7).reduce((sum, item) => sum + (item[key] || 0), 0);
    if (previous === 0) return { trend: 'neutral', value: 'N/A' };
    const change = ((recent - previous) / previous * 100).toFixed(1);
    return {
      trend: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
      value: `${Math.abs(change)}%`
    };
  };

  const consommationTrend = calculateTrend(consommationData, 'montant');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtres de période */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">📊 Analytics & Statistiques</h2>
          <p className="text-gray-600 text-sm mt-1">Analyse détaillée de vos données financières</p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map((days) => (
            <Button
              key={days}
              onClick={() => setPeriodDays(days)}
              variant={periodDays === days ? 'default' : 'outline'}
              size="sm"
              className={periodDays === days ? 'bg-violet-600 hover:bg-violet-700' : ''}
            >
              {days} jours
            </Button>
          ))}
        </div>
      </div>

      {/* Cartes statistiques animées */}
      {coutTotal && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="💰 Coût Total"
            value={coutTotal.cout_total}
            icon="💳"
            gradient="from-violet-500 to-violet-600"
            subtitle={`${periodDays} derniers jours`}
            trend={consommationTrend.trend}
            trendValue={consommationTrend.value}
          />
          
          <StatCard
            title="📤 Avances Versées"
            value={coutTotal.total_avances}
            icon="💸"
            gradient="from-blue-500 to-blue-600"
            subtitle={`${coutTotal.nb_avances} transactions`}
          />
          
          <StatCard
            title="💼 Commissions"
            value={coutTotal.total_commissions}
            icon="💰"
            gradient="from-orange-500 to-orange-600"
            subtitle={`Taux: ${coutTotal.taux_commission}%`}
          />
          
          <StatCard
            title="👥 Coût Moyen/Employé"
            value={coutTotal.cout_par_employe}
            icon="👤"
            gradient="from-pink-500 to-pink-600"
            subtitle={`${coutTotal.nb_employes} employés actifs`}
          />
        </div>
      )}

      {/* Graphiques principaux */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Consommation Quotidienne */}
        <Card className="shadow-lg hover:shadow-xl transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center">
              <span className="mr-2">📊</span>
              Consommation Quotidienne
              <span className="ml-2 text-sm font-normal text-gray-500">({periodDays} jours)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {consommationData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={consommationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    stroke="#6b7280"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    style={{ fontSize: '12px' }}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={formatDate}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Bar 
                    dataKey="montant" 
                    name="Montant (GNF)"
                    fill="#8b5cf6" 
                    radius={[8, 8, 0, 0]}
                    animationDuration={1000}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="text-4xl mb-3">📊</p>
                <p>Aucune donnée disponible</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Évolution du Solde */}
        <Card className="shadow-lg hover:shadow-xl transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <span className="mr-2">💰</span>
                Évolution du Solde
              </span>
              <span className="text-lg font-bold text-violet-600">{formatCurrency(soldeActuel)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {evolutionSolde.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={evolutionSolde}>
                  <defs>
                    <linearGradient id="colorSolde" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    stroke="#6b7280"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    style={{ fontSize: '12px' }}
                    tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={formatDate}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="solde" 
                    name="Solde (GNF)"
                    stroke="#8b5cf6" 
                    fillOpacity={1}
                    fill="url(#colorSolde)"
                    strokeWidth={3}
                    animationDuration={1500}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="text-4xl mb-3">💰</p>
                <p>Aucune donnée disponible</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Répartition Coûts & Top Employés */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition des coûts (Pie Chart) */}
        <Card className="shadow-lg hover:shadow-xl transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center">
              <span className="mr-2">🥧</span>
              Répartition des Coûts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      animationDuration={1000}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
                
                {/* Légende */}
                <div className="grid grid-cols-2 gap-4 mt-4 w-full">
                  <div className="bg-violet-50 rounded-lg p-3">
                    <div className="flex items-center mb-1">
                      <div className="w-3 h-3 rounded-full bg-violet-600 mr-2"></div>
                      <span className="text-sm font-medium text-gray-700">Avances</span>
                    </div>
                    <p className="text-lg font-bold text-violet-600">{formatCurrency(coutTotal?.total_avances || 0)}</p>
                  </div>
                  <div className="bg-pink-50 rounded-lg p-3">
                    <div className="flex items-center mb-1">
                      <div className="w-3 h-3 rounded-full bg-pink-600 mr-2"></div>
                      <span className="text-sm font-medium text-gray-700">Commissions</span>
                    </div>
                    <p className="text-lg font-bold text-pink-600">{formatCurrency(coutTotal?.total_commissions || 0)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="text-4xl mb-3">🥧</p>
                <p>Aucune donnée disponible</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top 10 Employés (version compacte) */}
        <Card className="shadow-lg hover:shadow-xl transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center">
              <span className="mr-2">🏆</span>
              Top Employés Demandeurs
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topEmployes.length > 0 ? (
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                {topEmployes.slice(0, 5).map((employe, index) => {
                  const maxMontant = topEmployes[0].montant_total;
                  const percentage = (employe.montant_total / maxMontant) * 100;
                  
                  return (
                    <div key={employe.employe_id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className={`
                            flex items-center justify-center w-8 h-8 rounded-full font-bold text-white text-sm
                            ${index === 0 ? 'bg-yellow-500' : ''}
                            ${index === 1 ? 'bg-gray-400' : ''}
                            ${index === 2 ? 'bg-orange-600' : ''}
                            ${index > 2 ? 'bg-gray-300 text-gray-700' : ''}
                          `}>
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 text-sm">{employe.nom_complet}</p>
                            <p className="text-xs text-gray-500">
                              {employe.nb_avances} avances
                            </p>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <p className="font-bold text-gray-900 text-sm">{formatCurrency(employe.montant_total)}</p>
                        </div>
                      </div>
                      
                      {/* Barre de progression */}
                      <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="absolute top-0 left-0 h-full bg-gradient-to-r from-violet-500 to-violet-600 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="text-4xl mb-3">👥</p>
                <p className="text-sm">Aucune donnée disponible</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tableau détaillé Top 10 */}
      {topEmployes.length > 0 && (
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center">
              <span className="mr-2">📋</span>
              Classement Détaillé - Top 10 Employés
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Rang</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Employé</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 text-sm">Montant Total</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Nb Avances</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Fréquence/Mois</th>
                  </tr>
                </thead>
                <tbody>
                  {topEmployes.map((employe, index) => (
                    <tr key={employe.employe_id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <div className={`
                          inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-white text-xs
                          ${index === 0 ? 'bg-yellow-500' : ''}
                          ${index === 1 ? 'bg-gray-400' : ''}
                          ${index === 2 ? 'bg-orange-600' : ''}
                          ${index > 2 ? 'bg-gray-300 text-gray-700' : ''}
                        `}>
                          {index + 1}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-medium text-gray-900">{employe.nom_complet}</p>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <p className="font-bold text-violet-600">{formatCurrency(employe.montant_total)}</p>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {employe.nb_avances}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-sm text-gray-600">{employe.frequence.toFixed(1)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EntrepriseAnalyticsTab;
